import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, Users, Wrench, Settings, TrendingUp, AlertTriangle,
  ArrowLeft, BarChart3, ShieldAlert, Package, UserCheck, Clock, Download, FileText, Newspaper, Database, ShieldCheck, Bell
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { getAuthHeaders } from "@/utils/auth";
import AdminNotificationBell from "@/components/AdminNotificationBell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [insuranceStats, setInsuranceStats] = useState(null);
  const [openCases, setOpenCases] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      // Check admin status
      const checkRes = await fetch(`${API}/admin/check`, { headers: getAuthHeaders() });
      if (!checkRes.ok) {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);

      // Load analytics
      const analyticsRes = await fetch(`${API}/admin/analytics`, { headers: getAuthHeaders() });
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }
      
      // Load insurance stats
      const insuranceRes = await fetch(`${API}/admin/insurance/stats`, { headers: getAuthHeaders() });
      if (insuranceRes.ok) {
        const data = await insuranceRes.json();
        setInsuranceStats(data);
      }

      // Load open case count
      const casesRes = await fetch(`${API}/admin/case-files?status=open`, { headers: getAuthHeaders() });
      if (casesRes.ok) {
        const data = await casesRes.json();
        setOpenCases(data.length);
      }
    } catch (error) {
      console.error("Admin load error:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" data-testid="back-to-app-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to App
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-7 h-7 text-primary" />
              <span className="font-heading text-xl font-bold uppercase tracking-wider">Admin Panel</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <AdminNotificationBell />
            <span className="text-sm text-muted-foreground">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Total Users</p>
                  <p className="text-3xl font-heading font-bold">{analytics?.users?.total || 0}</p>
                  <p className="text-xs text-green-400">+{analytics?.users?.recent_30_days || 0} this month</p>
                </div>
                <Users className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Total Tools</p>
                  <p className="text-3xl font-heading font-bold">{analytics?.tools?.total || 0}</p>
                  <p className="text-xs text-green-400">+{analytics?.tools?.recent_30_days || 0} this month</p>
                </div>
                <Package className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Stolen Reports</p>
                  <p className="text-3xl font-heading font-bold text-red-400">
                    {analytics?.tools?.by_status?.Stolen || 0}
                  </p>
                </div>
                <ShieldAlert className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm uppercase tracking-wide">Verified Users</p>
                  <p className="text-3xl font-heading font-bold text-green-400">
                    {analytics?.users?.verified || 0}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation Cards */}
        <h2 className="font-heading text-xl uppercase mb-4">Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Link to="/admin/users" data-testid="admin-users-link">
            <Card className="border-border bg-card hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Users className="w-12 h-12 text-primary mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">User Management</h3>
                <p className="text-muted-foreground text-sm">
                  View, suspend, reset passwords, and manage user accounts
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/tools" data-testid="admin-tools-link">
            <Card className="border-border bg-card hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Wrench className="w-12 h-12 text-primary mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">Tool Oversight</h3>
                <p className="text-muted-foreground text-sm">
                  Search tools, view stolen reports, transfer ownership
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/news" data-testid="admin-news-link">
            <Card className="border-green-700/50 bg-green-900/10 hover:border-green-500 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Newspaper className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">News & Stories</h3>
                <p className="text-muted-foreground text-sm">
                  Create and manage news articles with rich content
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/police" data-testid="admin-police-link">
            <Card className="border-blue-700/50 bg-blue-900/10 hover:border-blue-500 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Shield className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">Police Portal</h3>
                <p className="text-muted-foreground text-sm">
                  Manage law enforcement portal accounts
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/cases" data-testid="admin-cases-link">
            <Card className="border-red-700/50 bg-red-900/10 hover:border-red-500 transition-colors cursor-pointer h-full relative">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <FileText className="w-12 h-12 text-red-400 mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">Case Files</h3>
                <p className="text-muted-foreground text-sm">
                  Manage stolen tool case files and track investigations
                </p>
                {openCases > 0 && (
                  <Badge className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2">
                    {openCases} open
                  </Badge>
                )}
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/settings" data-testid="admin-settings-link">
            <Card className="border-border bg-card hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Settings className="w-12 h-12 text-primary mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">System Settings</h3>
                <p className="text-muted-foreground text-sm">
                  Configure manufacturers, tool types, and system limits
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/logs" data-testid="admin-logs-link">
            <Card className="border-border bg-card hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Clock className="w-12 h-12 text-primary mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">Activity Logs</h3>
                <p className="text-muted-foreground text-sm">
                  View admin actions and audit trail
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/backups" data-testid="admin-backups-link">
            <Card className="border-border bg-card hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Database className="w-12 h-12 text-primary mb-4" />
                <h3 className="font-heading text-lg uppercase mb-2">Backups</h3>
                <p className="text-muted-foreground text-sm">
                  Manage automated backups and restore data
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Insurance Referrals Section */}
        <h2 className="font-heading text-xl uppercase mb-4 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          Insurance Referrals
          <Badge className="bg-yellow-500 text-black text-xs">Coming Soon</Badge>
        </h2>
        <Card className="border-emerald-700/50 bg-emerald-900/10 mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-muted-foreground text-sm uppercase tracking-wide mb-1">Total Clicks</p>
                <p className="text-4xl font-heading font-bold text-emerald-400">
                  {insuranceStats?.total_clicks || 0}
                </p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground text-sm uppercase tracking-wide mb-1">Last {insuranceStats?.days || 30} Days</p>
                <p className="text-4xl font-heading font-bold text-emerald-400">
                  {insuranceStats?.clicks_last_n_days || 0}
                </p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground text-sm uppercase tracking-wide mb-1">Top Source</p>
                <p className="text-xl font-heading font-bold text-emerald-400">
                  {insuranceStats?.clicks_by_source?.[0]?.source || "N/A"}
                </p>
              </div>
            </div>
            
            {insuranceStats?.recent_clicks?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-emerald-800/50">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-muted-foreground">Recent Clicks:</p>
                  <InsuranceExportButton />
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {insuranceStats.recent_clicks.slice(0, 5).map((click, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-emerald-950/50 rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-300">{click.referral_source}</span>
                        {click.user_email && (
                          <span className="text-xs text-emerald-500">({click.user_email})</span>
                        )}
                        {!click.logged_in && click.logged_in !== undefined && (
                          <span className="text-xs text-gray-500">(Guest)</span>
                        )}
                      </div>
                      <span className="text-muted-foreground text-xs">
                        {new Date(click.timestamp).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Reports Section */}
        <h2 className="font-heading text-xl uppercase mb-4">Export Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <ExportCard 
            title="Users Report" 
            description="Export all user accounts" 
            endpoint="/admin/export/users"
            filename="users_report"
          />
          <ExportCard 
            title="All Tools Report" 
            description="Export all registered tools" 
            endpoint="/admin/export/tools"
            filename="all_tools_report"
          />
          <ExportCard 
            title="Stolen Tools Report" 
            description="Export stolen tools list" 
            endpoint="/admin/export/stolen"
            filename="stolen_tools_report"
          />
          <ExportCard 
            title="Activity Log" 
            description="Export admin activity log" 
            endpoint="/admin/export/activity"
            filename="admin_activity_log"
          />
        </div>

        {/* Analytics Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Manufacturers */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Top Manufacturers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.top_manufacturers?.map((mfg, index) => (
                  <div key={mfg._id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground text-sm w-6">{index + 1}.</span>
                      <span className="font-medium">{mfg._id}</span>
                    </div>
                    <span className="font-mono text-primary">{mfg.count}</span>
                  </div>
                ))}
                {(!analytics?.top_manufacturers || analytics.top_manufacturers.length === 0) && (
                  <p className="text-muted-foreground text-sm">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Tool Types */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                <Wrench className="w-5 h-5 text-primary" />
                Top Tool Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.top_tool_types?.map((type, index) => (
                  <div key={type._id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground text-sm w-6">{index + 1}.</span>
                      <span className="font-medium">{type._id}</span>
                    </div>
                    <span className="font-mono text-primary">{type.count}</span>
                  </div>
                ))}
                {(!analytics?.top_tool_types || analytics.top_tool_types.length === 0) && (
                  <p className="text-muted-foreground text-sm">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tools by Status */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Tools by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                  <p className="text-xs text-muted-foreground uppercase">Active</p>
                  <p className="text-2xl font-heading font-bold text-green-400">
                    {analytics?.tools?.by_status?.Active || 0}
                  </p>
                </div>
                <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
                  <p className="text-xs text-muted-foreground uppercase">Stolen</p>
                  <p className="text-2xl font-heading font-bold text-red-400">
                    {analytics?.tools?.by_status?.Stolen || 0}
                  </p>
                </div>
                <div className="p-3 bg-gray-500/10 rounded border border-gray-500/30">
                  <p className="text-xs text-muted-foreground uppercase">Sold</p>
                  <p className="text-2xl font-heading font-bold text-gray-400">
                    {analytics?.tools?.by_status?.Sold || 0}
                  </p>
                </div>
                <div className="p-3 bg-gray-600/10 rounded border border-gray-600/30">
                  <p className="text-xs text-muted-foreground uppercase">Discarded</p>
                  <p className="text-2xl font-heading font-bold text-gray-500">
                    {analytics?.tools?.by_status?.Discarded || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Users */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Most Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics?.top_users?.map((user, index) => (
                  <div key={user._id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground text-sm w-6">{index + 1}.</span>
                      <div>
                        <p className="font-medium text-sm">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <span className="font-mono text-primary">{user.count} tools</span>
                  </div>
                ))}
                {(!analytics?.top_users || analytics.top_users.length === 0) && (
                  <p className="text-muted-foreground text-sm">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function ExportCard({ title, description, endpoint, filename }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}${endpoint}`, { headers: getAuthHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`${title} exported!`);
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="border-border bg-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-heading uppercase text-sm mb-1">{title}</h4>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleExport}
            disabled={exporting}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            data-testid={`export-${filename}-btn`}
          >
            {exporting ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function InsuranceExportButton() {
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showSelector, setShowSelector] = useState(false);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(
        `${API}/admin/insurance/export?month=${selectedMonth}&year=${selectedYear}`, 
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `insurance_clicks_${months[selectedMonth - 1]}_${selectedYear}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Insurance report exported!`);
        setShowSelector(false);
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="relative">
      <Button 
        size="sm" 
        variant="outline" 
        onClick={() => setShowSelector(!showSelector)}
        className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30 text-xs"
      >
        <Download className="w-3 h-3 mr-1" />
        Export Monthly Report
      </Button>
      
      {showSelector && (
        <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-emerald-700 rounded-lg p-4 z-50 shadow-xl min-w-[200px]">
          <p className="text-xs text-emerald-400 mb-2 font-semibold">Select Month:</p>
          <div className="flex gap-2 mb-3">
            <select 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="flex-1 bg-slate-900 border border-emerald-800 rounded px-2 py-1 text-sm text-white"
            >
              {months.map((m, idx) => (
                <option key={m} value={idx + 1}>{m}</option>
              ))}
            </select>
            <select 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="bg-slate-900 border border-emerald-800 rounded px-2 py-1 text-sm text-white"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={handleExport}
              disabled={exporting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            >
              {exporting ? "Exporting..." : "Download CSV"}
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => setShowSelector(false)}
              className="text-gray-400 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
